import React from "react";
import { Edit2, Check, X } from "react-feather";
import BaseTooltip from "../BaseTooltip";

/* ── Accent colours per section (matched by iconColor) ── */
const ACCENT_GRADIENTS = {
  "#6366f1": "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "#8b5cf6": "linear-gradient(135deg,#8b5cf6,#a78bfa)",
  "#f43f5e": "linear-gradient(135deg,#f43f5e,#fb7185)",
  "#f59e0b": "linear-gradient(135deg,#f59e0b,#fbbf24)",
  "#10b981": "linear-gradient(135deg,#10b981,#34d399)",
};

/**
 * Shared section card for profile sub-sections.
 */
export const ProfileSection = ({
  id,
  title,
  icon: Icon,
  iconColor = "#6366f1",
  viewMode,
  editMode,
  onEdit,
  onDiscard,
  isEditable = true,
  isProfile = false,
  btnLoader = false,
  additionalActions,
  children,
  animDelay = "0ms",
  isSaveDisabled = false,
}) => {
  const gradient =
    ACCENT_GRADIENTS[iconColor] || `linear-gradient(135deg,${iconColor},${iconColor}99)`;

  return (
    <div
      id={id}
      className="animate-slide-up"
      style={{
        background: "var(--glass-bg)",
        border: "1px solid var(--glass-border)",
        borderRadius: 16,
        overflow: "hidden",
        marginBottom: 16,
        animationDelay: animDelay,
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 4px 20px ${iconColor}18`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Coloured top accent bar */}
      <div style={{ height: 3, background: gradient }} />

      {/* Section header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          borderBottom: "1px solid var(--glass-border)",
          background: `${iconColor}06`,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {Icon && (
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: `${iconColor}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon size={16} strokeWidth={2} style={{ color: iconColor }} />
            </div>
          )}
          <h3
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h3>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {viewMode && additionalActions && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {additionalActions}
            </div>
          )}
          {viewMode && isEditable && !isProfile && (
            <button
              type="button"
              onClick={onEdit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 14px",
                borderRadius: 8,
                background: "rgba(245,158,11,0.1)",
                border: "1px solid rgba(245,158,11,0.25)",
                color: "#f59e0b",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(245,158,11,0.18)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(245,158,11,0.1)";
              }}
            >
              <Edit2 size={13} strokeWidth={2} /> Edit
            </button>
          )}
          {editMode && (
            <>
              <button
                type="button"
                onClick={onDiscard}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "6px 12px",
                  borderRadius: 8,
                  background: "var(--icon-btn-bg)",
                  border: "1px solid var(--glass-border)",
                  color: "var(--text-secondary)",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <X size={13} strokeWidth={2} /> Discard
              </button>
              <button
                type="submit"
                disabled={btnLoader || isSaveDisabled}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "6px 16px",
                  borderRadius: 8,
                  background: gradient,
                  border: "none",
                  color: "#fff",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  cursor: btnLoader || isSaveDisabled ? "not-allowed" : "pointer",
                  opacity: btnLoader || isSaveDisabled ? 0.7 : 1,
                  boxShadow: `0 4px 12px ${iconColor}40`,
                  transition: "all 0.15s",
                }}
              >
                {btnLoader ? (
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTopColor: "#fff",
                      animation: "spin 0.7s linear infinite",
                      display: "inline-block",
                    }}
                  />
                ) : (
                  <Check size={13} strokeWidth={2.5} />
                )}
                Save
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "20px 24px" }}>{children}</div>
    </div>
  );
};

/**
 * Read-only field display — label + value pair.
 */
export const InfoField = ({
  label,
  value,
  required = false,
  mono = false,
  action = null,
  sideAction = null,
}) => {
  const isStringValue = typeof value === "string" && value.trim().length > 0;

  const content = (
    <div
      style={{
        fontSize: "0.9rem",
        fontWeight: 600,
        color: value ? "var(--text-primary)" : "var(--text-muted)",
        fontFamily: mono ? "'SF Mono', 'Fira Code', monospace" : undefined,
        wordBreak: "break-all",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        padding: "6px 10px",
        background: value ? "var(--icon-btn-bg)" : "transparent",
        borderRadius: 8,
        border: value ? "1px solid var(--glass-border)" : "none",
        display: "inline-flex",
        alignItems: "center",
        minWidth: value ? 80 : undefined,
        minHeight: 35,
        boxSizing: "border-box",
        maxWidth: "100%",
      }}
    >
      {value || "—"}
    </div>
  );

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          fontSize: "0.6875rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.09em",
          color: "var(--text-muted)",
          marginBottom: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: action ? "space-between" : "flex-start",
          gap: 3,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
          {label}
          {required && <span style={{ color: "#f43f5e" }}>*</span>}
        </span>
        {action}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "nowrap",
          maxWidth: "100%",
        }}
      >
        <div style={{ maxWidth: sideAction ? "calc(100% - 42px)" : "100%", overflow: "hidden" }}>
          {isStringValue ? (
            <BaseTooltip text={value} position="top">
              {content}
            </BaseTooltip>
          ) : (
            content
          )}
        </div>
        {sideAction && (
          <div style={{ flexShrink: 0, display: "inline-flex", alignItems: "center" }}>
            {sideAction}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Section divider with optional label.
 */
export const SectionDivider = ({ label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      margin: "16px 0 20px",
    }}
  >
    {label && (
      <span
        style={{
          fontSize: "0.6875rem",
          fontWeight: 800,
          color: "var(--brand-indigo)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          whiteSpace: "nowrap",
          padding: "3px 10px",
          background: "rgba(99,102,241,0.08)",
          borderRadius: 6,
          border: "1px solid rgba(99,102,241,0.15)",
        }}
      >
        {label}
      </span>
    )}
    <div style={{ flex: 1, height: 1, background: "var(--glass-border)" }} />
  </div>
);

export default ProfileSection;
